import { useRef, useState } from 'react';
import { Calendar, FileCheck, Upload } from 'lucide-react';

interface Props {
  file: File | null;
  onFile: (f: File | null) => void;
  /** PDF già caricato in bozza (se nessun nuovo file selezionato). */
  pendingUrl: string | null;
  signingDate: Date;
  onSigningDate: (d: Date) => void;
}

function fmt(date: Date): string {
  const d = date instanceof Date && !isNaN(date.getTime()) ? date : new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Step 3 del wizard: upload del PDF firmato + data firma. */
export function AcceptanceStepContract({ file, onFile, pendingUrl, signingDate, onSigningDate }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  const today = fmt(new Date());

  const pickFile = (f: File | null) => {
    if (f && f.type !== 'application/pdf') return;
    onFile(f);
  };

  return (
    <div className="space-y-4">
      <div
        className={`border-2 border-dashed ${drag ? 'border-[#1FA855] bg-[#25D366]/[0.06]' : 'border-black/20 dark:border-gray-600'} rounded-lg p-6 text-center transition-colors`}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
        onDrop={(e) => { e.preventDefault(); setDrag(false); pickFile(e.dataTransfer.files?.[0] ?? null); }}
      >
        <input type="file" accept=".pdf" ref={inputRef} className="hidden"
          onChange={(e) => pickFile(e.target.files?.[0] ?? null)} />
        {file ? (
          <div className="space-y-1">
            <FileCheck className="h-9 w-9 text-[#1FA855] mx-auto" />
            <p className="text-sm font-medium text-[#37352F] dark:text-white">{file.name}</p>
            <p className="text-xs text-[#6B6762]">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
            <button type="button" onClick={() => onFile(null)} className="text-xs text-[#EB5757] hover:underline">Rimuovi</button>
          </div>
        ) : pendingUrl ? (
          <div className="space-y-1">
            <FileCheck className="h-9 w-9 text-[#1FA855] mx-auto" />
            <p className="text-sm font-medium text-[#37352F] dark:text-white">PDF firmato già caricato in bozza</p>
            <button type="button" onClick={() => inputRef.current?.click()} className="text-xs text-[#2383E2] hover:underline">Sostituisci</button>
          </div>
        ) : (
          <div className="space-y-1 cursor-pointer" onClick={() => inputRef.current?.click()}>
            <Upload className={`h-9 w-9 mx-auto ${drag ? 'text-[#1FA855]' : 'text-black/40 dark:text-white/40'}`} />
            <p className="text-sm font-medium text-[#37352F] dark:text-white">Seleziona il contratto firmato (PDF)</p>
            <p className="text-xs text-[#6B6762]">o trascinalo qui — max 10MB</p>
          </div>
        )}
      </div>

      <div>
        <label className="flex items-center text-[12px] font-medium text-[#37352F] dark:text-[#FFFFFFCF] mb-1">
          <Calendar className="h-3.5 w-3.5 mr-1.5" /> Data di firma del contratto
        </label>
        <input
          type="date" value={fmt(signingDate)} max={today}
          onChange={(e) => { const d = new Date(e.target.value); if (!isNaN(d.getTime())) onSigningDate(d); }}
          className="w-full max-w-xs px-3 py-2 text-sm border border-[rgba(55,53,47,0.16)] dark:border-[#454B4E] rounded-md bg-white dark:bg-[#2F3437] text-[#37352F] dark:text-white focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] outline-none"
        />
      </div>
    </div>
  );
}
